"use client";

import { useEffect } from "react";
import { estErreurDeChunk, rechargerApresErreurDeChunk } from "@/lib/pwa/chunk-error";

/**
 * Error boundary racine (segment app). Catche les erreurs des pages ET
 * des layouts enfants (budget, music, etc.) — y compris un crash dans
 * BudgetShell. Rendu DANS le layout racine → la classe `.dark` posée par
 * le themeBootScript persiste, donc le thème (dark/light) est préservé
 * (avant : un crash tombait sur l'écran d'erreur natif iOS, en clair).
 */
export default function AppError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // eslint-disable-next-line no-console
    console.error("[app] error boundary:", error);
    // Morceau de l'app introuvable (page ouverte avant un déploiement) : un
    // rechargement règle le problème — une fois, jamais en boucle.
    if (process.env.NODE_ENV === "production" && estErreurDeChunk(error)) {
      rechargerApresErreurDeChunk();
    }
  }, [error]);

  return (
    <div className="min-h-dvh flex flex-col items-center justify-center gap-4 p-6 text-center bg-background text-foreground">
      <div className="w-12 h-12 rounded-full bg-negative/15 flex items-center justify-center">
        <svg className="w-6 h-6 text-negative" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
      </div>
      <div className="space-y-1">
        <p className="text-base font-semibold">Une erreur est survenue</p>
        <p className="text-xs text-text-muted max-w-sm break-words">
          {error?.message || "Quelque chose s'est mal passé."}
        </p>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => reset()}
          className="px-4 py-2 rounded-xl bg-forest text-white text-sm font-medium hover:bg-forest-dark transition-colors"
        >
          Réessayer
        </button>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="px-4 py-2 rounded-xl border border-border-default text-sm font-medium hover:bg-surface-secondary transition-colors"
        >
          Recharger
        </button>
      </div>
    </div>
  );
}
